import { getCurrentWindowId, ResponseType } from 'utils'
import { clientActionEvent, clientActionSubscribe } from './ClientAction'
import {
  CollectionEventAssignOrders,
  OrderEventCloneBulk,
  OrderEventCreateBulk,
  OrderEventModify,
  OrderEventPrepareBulk,
  OrderEventPrepareCollection,
  OrderEventSubmit,
  OrderEventSubmitBulk,
  OrderEventUpdatePreAssignmentAttributes
} from './generated'
import {
  CompositeEvent,
  EntityType,
  EntityTypeEnum,
  EventSpecificArgs,
  OrderSubscriptionIdentifier,
  SubscribeType,
  SubscriptionFilter
} from 'api'
import { Locale, Translation } from 'locale'
import { PostTransformSubscriptionFilters } from 'model'

export type OrderCreateArgs = Omit<OrderEventCreateBulk, 'eventType' | 'correlationId' | 'ignoreClearAxeId'>

function orderCreate(args: OrderCreateArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventCreateBulk', args, sourceWindowId)
}

function ordersPageSubscribe(subscriptionIdentifier: OrderSubscriptionIdentifier, filters: SubscriptionFilter[], _pageNumber: number = 0) {
  return clientActionSubscribe({
    entityTypeEnum: EntityTypeEnum.ORDER,
    filters,
    preDefinedFilter: null,
    postTransformFilters: PostTransformSubscriptionFilters[subscriptionIdentifier],
    subscribeType: SubscribeType.FILTERED,
    subscriptionIdentifier,
    limitResults: false,
    pageSize: null,
    pageNumber: null,
    summaryRequest: null
  })
}

function orderCreateWithResponse(args: OrderCreateArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventCreateBulk', args, sourceWindowId, undefined, {
    successResponse: ResponseType.GROWL,
    successMessage: Locale.translate(Translation.SC_NOTIFICATION_UPLOAD_SUCCESS),
    errorResponse: ResponseType.DIALOG
  })
}

export type OrderModifyArgs = Omit<OrderEventModify, 'eventType' | 'correlationId' | 'ignoreClearAxeId'>

function orderModify(args: OrderModifyArgs, sourceWindowId: string = getCurrentWindowId(), correlationId?: string) {
  return clientActionEvent(
    'OrderEventModify',
    args,
    sourceWindowId,
    {
      entityIds: [args.orderId],
      entityType: EntityType.ORDER
    },
    undefined,
    correlationId
  )
}

export type OrderModifyBulkArgs = EventSpecificArgs<CompositeEvent>

const orderModifyBulk = (args: OrderModifyBulkArgs, sourceWindowId: string = getCurrentWindowId()) =>
  clientActionEvent('CompositeEvent', args, sourceWindowId)

export type OrderSubmitArgs = Omit<OrderEventSubmit, 'eventType' | 'correlationId'>

function orderSubmit(args: OrderSubmitArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventSubmit', args, sourceWindowId, {
    entityIds: [args.orderId],
    entityType: EntityType.ORDER
  })
}

export type OrderBulkPrepareArgs = Omit<OrderEventPrepareBulk, 'eventType' | 'correlationId'>

function orderBulkPrepare(args: OrderBulkPrepareArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventPrepareBulk', args, sourceWindowId, {
    entityIds: args.orderCounterpartyInstructions.map(orderCounterpartyInstruction => orderCounterpartyInstruction.orderId),
    entityType: EntityType.ORDER
  })
}

export type OrderPrepareCollectionArgs = Omit<OrderEventPrepareCollection, 'eventType' | 'correlationId'>

function orderPrepareCollection(args: OrderPrepareCollectionArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventPrepareCollection', args, sourceWindowId, {
    entityIds: Object.keys(args.collectionId),
    entityType: EntityType.ORDER
  })
}

export type OrderBulkSubmitArgs = Omit<OrderEventSubmitBulk, 'eventType' | 'correlationId'>

function orderBulkSubmit(args: OrderBulkSubmitArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent(
    'OrderEventSubmitBulk',
    args,
    sourceWindowId,
    {
      entityIds: args.orderIds,
      entityType: EntityType.ORDER
    },
    {
      successResponse: ResponseType.GROWL,
      successMessage: Locale.translate(Translation.SC_NOTIFICATION_RFQ_SEND_SUCCESS),
      errorResponse: ResponseType.GROWL,
      errorRetry: true
    }
  )
}

function orderDelete(orderId: string, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventDelete', { orderId }, sourceWindowId, {
    entityIds: [orderId],
    entityType: EntityType.ORDER
  })
}

export type OrderBulkCloneArgs = Omit<OrderEventCloneBulk, 'eventType' | 'correlationId'>

function orderBulkClone(args: OrderBulkCloneArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventCloneBulk', args, sourceWindowId)
}

export type OrderAssignToCollectionArgs = Omit<CollectionEventAssignOrders, 'eventType' | 'correlationId'>

function orderAssignToCollection(args: OrderAssignToCollectionArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('CollectionEventAssignOrders', args, sourceWindowId, {
    entityIds: args.orderIds,
    entityType: EntityType.ORDER
  })
}

function orderRemoveFromCollection(orderIds: string[], sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent(
    'CollectionEventAssignOrders',
    {
      orderIds,
      collectionId: null,
      newCollectionName: null
    },
    sourceWindowId,
    {
      entityIds: orderIds,
      entityType: EntityType.ORDER
    }
  )
}

export type OrderUpdatePreAssignmentAttributesArgs = Omit<OrderEventUpdatePreAssignmentAttributes, 'eventType' | 'correlationId'>

function orderUpdatePreAssignmentAttributes(args: OrderUpdatePreAssignmentAttributesArgs, sourceWindowId: string = getCurrentWindowId()) {
  return clientActionEvent('OrderEventUpdatePreAssignmentAttributes', args, sourceWindowId, {
    entityIds: [args.orderId],
    entityType: EntityType.ORDER
  })
}

export const OrderActions = {
  create: orderCreate,
  createWithResponse: orderCreateWithResponse,
  modify: orderModify,
  bulkModify: orderModifyBulk,
  submit: orderSubmit,
  bulkPrepare: orderBulkPrepare,
  prepareCollection: orderPrepareCollection,
  bulkSubmit: orderBulkSubmit,
  delete: orderDelete,
  pageSubscribe: ordersPageSubscribe,
  bulkClone: orderBulkClone,
  assignToCollection: orderAssignToCollection,
  removeFromCollection: orderRemoveFromCollection,
  updatePreAssignment: orderUpdatePreAssignmentAttributes
}
